import { Button, IconButton } from '@mui/material';
import { Box, useMediaQuery, useTheme } from '@mui/system';
import { evaluateConditionGroup } from '../conditionUtils';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { FieldTemplate, FormStatus, Patient } from 'api/models';
import { useTranslation } from 'react-i18next';
import { DialogTypes, GeneralIds } from 'types/types';
import {
  dialogState,
  formTemplateState,
  ToastSeverity,
  toastState,
} from 'state';
import { Dispatch, SetStateAction } from 'react';
import { FormValues, onSaveProps } from '../FormLayout';
import { FormikProps } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import { Drafts, Save, Upload } from '@mui/icons-material';
import { VisuallyHiddenInput } from 'routes/ProjectFormLists/FormTemplateTypesTable';
import { uploadHl7 } from 'api';
import { FormSaveButton } from 'components/FormSaveButton/FormSaveButton';

interface FormControlsProps {
  setShouldShowLeaveConfirmation: Dispatch<SetStateAction<boolean>>;
  onSave: ({
    values,
    showToast,
    status,
  }: onSaveProps) => Promise<string | undefined>;
  formik: FormikProps<FormValues>;
  fieldTemplates?: FieldTemplate[];
  patient?: Patient;
  readOnly?: boolean;
}

export default function FormControls({
  onSave,
  setShouldShowLeaveConfirmation,
  formik,
  fieldTemplates = [],
  patient,
  readOnly,
}: FormControlsProps) {
  const [{ isOpen }, openDialog] = useRecoilState(dialogState);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { formId, visitId } = useParams<GeneralIds>();
  const setToast = useSetRecoilState(toastState);
  const formTemplate = useRecoilValue(formTemplateState);
  const isHL7Enabled = formTemplate?.mappings?.externalCommunication
    ?.hl7Identifier
    ? true
    : false;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const onUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Object.assign({}, e.target.files);
    const file = files[0];
    if (!file) {
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await onSave({
          values: formik.values,
          showToast: false,
          status: FormStatus.Filling,
        });
        await uploadHl7({
          formId: formId ?? '',
          visitId: visitId ?? '',
          hl7Text: reader.result as string,
        });
        setToast({
          isOpen: true,
          message: t('general.success.importWithReload'),
          severity: ToastSeverity.success,
        });
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } catch (e: any) {
        setToast({
          isOpen: true,
          message: e.response?.data?.error?.[0].message,
          severity: ToastSeverity.error,
        });
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleSave = () => {
    setShouldShowLeaveConfirmation(false);
    onSave({
      values: formik.values,
      showToast: true,
      status: FormStatus.Filling,
    });
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        top: -3,
        p: 2,
        right: 50,
        zIndex: isOpen ? 1 : 1201,
      }}
    >
      {!readOnly && isHL7Enabled && (
        <IconButton
          component="label"
          role={undefined}
          tabIndex={-1}
          title="Import"
        >
          <Upload />
          <VisuallyHiddenInput
            id="file_upload_to_ignore"
            name="file_upload_to_ignore"
            onChange={onUpload}
            type="file"
          />
        </IconButton>
      )}
      {!readOnly &&
        (isMobile ? (
          <IconButton
            sx={{
              mr: 1,
              ml: 1,
              background: theme => theme.palette.primary.main,
            }}
            onClick={handleSave}
          >
            <Save sx={{ color: 'white' }} />
          </IconButton>
        ) : (
          <Button
            color={'primary'}
            variant="contained"
            sx={{
              mr: 1,
              ml: 1,
            }}
            onClick={handleSave}
          >
            {isMobile ? <Drafts /> : t('general.saveAsDraft')}
          </Button>
        ))}

      {!readOnly && (
        <FormSaveButton
          key="form-save-button"
          isLoading={formik.isSubmitting}
          label={t('general.finalization')}
          sx={{
            background: theme.palette.success.main,
          }}
          onClick={async () => {
            setShouldShowLeaveConfirmation(false);
            const errors = await formik.validateForm();
            const visibleFieldsErrors = Object.keys(errors).filter(key => {
              return evaluateConditionGroup({
                rootGroup: fieldTemplates.find(field => field.id === key)
                  ?.isVisible,
                values: formik.values,
                fieldTemplates,
                patient,
              });
            });

            // the required field is hidden, we need to do a manual finalized save
            if (
              visibleFieldsErrors.length === 0 &&
              Object.keys(errors).length !== 0
            ) {
              return onSave({
                values: formik.values,
                showToast: true,
                status: FormStatus.Finalized,
              });
            }

            if (visibleFieldsErrors.length !== 0) {
              openDialog({
                isOpen: true,
                dialogType: DialogTypes.formValidationErrors,
                data: {
                  errors: errors,
                  fieldTemplates,
                  values: formik.values,
                },
              });
            }
          }}
        />
      )}

      {readOnly && (
        <Button
          variant="contained"
          color="warning"
          onClick={() => navigate(-1)}
        >
          {t('general.cancel')}
        </Button>
      )}
    </Box>
  );
}
